<script>
import CustomMultiselect from '@/common/components/Form/CustomMultiselect'
import CustomSelect from '@/common/components/Form/CustomSelect'
import { Validator } from 'vee-validate'
import { mapGetters } from 'vuex'

Validator.extend('alpha_dash_spaces', {
	getMessage: field => 'Le champ ' + field + ' ne peut contenir que des caractères des tirets et des espaces',
	validate: value => {
		for (let i = 0; i < value.length; i++) {
			if ('abcdefghijklmnopqrstuvwxyzçéèêîïë -'.indexOf(value[i].toLowerCase()) === -1) { return false }
		}
		return true
	}
})

export default {
	name: 'AddContact',
	components: {
		CustomSelect,
		CustomMultiselect
	},
	props: {
		data: {
			type: Object,
			default: () => ({})
		}
	},
	data () {
		return {
			user: {
				firstname: null,
				lastname: null,
				phone: null,
				birthdate: null,
				email: null,
				origin: null,
				centerOfInterests: [],
				type: 'Client',
				address: {
					line: null,
					zip: null,
					city: null,
					country: null
				}
			},
			prospectTypes: ['Client', 'Prospect'],
			origins: ['Site web', 'Téléphone', 'Email', 'Salon', 'Réseau']
		}
	},
	computed: {
		...mapGetters({
			interests: 'tags'
		}),
		defaultType () {
			return this.data && this.data.defaultType ? this.data.defaultType : 'Client'
		},
		selectedType () {
			return this.user.type || this.defaultType
		},
		typeLabel () {
			return this.selectedType === 'Prospect' ? 'prospect' : 'client'
		},
		typeLabelPlural () {
			return this.selectedType === 'Prospect' ? 'prospects' : 'clients'
		}
	},
	mounted () {
		this.user.origin = this.origins[0]
		if (this.data && this.data.defaultType) {
			this.user.type = this.data.defaultType
		}
	},
	methods: {
		close () {
			this.$emit('close')
		},
		addContact () {
			this.$validator.validate().then((validation) => {
				if (validation) {
					this.$store.dispatch('addClient', this.user).then(() => {
						this.$notify({
							type: 'success',
							group: 'default',
							title: 'Vous avez ajouté un nouveau contact',
							text: 'Il va recevoir un e-mail afin d\'activer son compte'
						})
						this.$emit('close')
					}).catch((error) => {
						this.$notify({
							type: 'error',
							group: 'default',
							title: 'Une erreur est survenue à l\'ajout du contact',
							text: (error.message === 'Email is already in use.') ? 'Cette adresse e-mail est déjà utilisée' : 'Veuillez vérifier que les informations que vous avez renseignées sont correctes'
						})
					})
				}
			})
		}
	}
}
</script>

<template lang="html">
  <div class="add-contact">
		<!-- Header -->
		<div class="add-contact__header">
			<div class="add-contact__header-left">
				<h2 class="add-contact__title">Ajouter un {{ typeLabel }}</h2>
				<span class="add-contact__subtitle">Remplissez les informations du contact</span>
			</div>
			<div class="add-contact__type-selector">
				<button
					v-for="type in prospectTypes"
					:key="type"
					class="add-contact__type-btn"
					:class="{ 'add-contact__type-btn--active': selectedType === type }"
					@click="user.type = type"
				>
					{{ type }}
				</button>
			</div>
		</div>

		<!-- Body -->
		<div class="add-contact__body">
			<form class="add-contact__form" @submit.prevent="addContact">

				<!-- Section Identité -->
				<div class="add-contact__section">
					<div class="add-contact__section-header">
						<svg class="add-contact__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
							<circle cx="12" cy="7" r="4"/>
						</svg>
						<span class="add-contact__section-title">Identité</span>
					</div>
					<div class="add-contact__grid add-contact__grid--2">
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('lastname') }">
							<label class="add-contact__label" for="lastname">Nom *</label>
							<input
								class="add-contact__input"
								v-validate="'required|alpha_dash_spaces'"
								type="text"
								name="lastname"
								id="lastname"
								placeholder="Dupont"
								v-model="user.lastname"
							>
							<span v-if="errors.has('lastname')" class="add-contact__error">{{ errors.first('lastname') }}</span>
						</div>
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('firstname') }">
							<label class="add-contact__label" for="firstname">Prénom *</label>
							<input
								class="add-contact__input"
								v-validate="'required|alpha_dash_spaces'"
								type="text"
								name="firstname"
								id="firstname"
								placeholder="Jean"
								v-model="user.firstname"
							>
							<span v-if="errors.has('firstname')" class="add-contact__error">{{ errors.first('firstname') }}</span>
						</div>
					</div>
					<div class="add-contact__grid add-contact__grid--2">
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('date de naissance') }">
							<label class="add-contact__label" for="birthdate">Date de naissance *</label>
							<input
								class="add-contact__input"
								v-validate="'required'"
								type="date"
								name="date de naissance"
								id="birthdate"
								v-model="user.birthdate"
							>
							<span v-if="errors.has('date de naissance')" class="add-contact__error">{{ errors.first('date de naissance') }}</span>
						</div>
						<div class="add-contact__field">
							<!-- Espace réservé pour équilibrer la grille -->
						</div>
					</div>
				</div>

				<!-- Section Contact -->
				<div class="add-contact__section">
					<div class="add-contact__section-header">
						<svg class="add-contact__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
						</svg>
						<span class="add-contact__section-title">Coordonnées</span>
					</div>
					<div class="add-contact__grid add-contact__grid--2">
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('phone') }">
							<label class="add-contact__label" for="phone">Téléphone *</label>
							<input
								class="add-contact__input"
								v-validate="'required'"
								type="tel"
								name="phone"
								id="phone"
								placeholder="06 12 34 56 78"
								v-model="user.phone"
							>
							<span v-if="errors.has('phone')" class="add-contact__error">{{ errors.first('phone') }}</span>
						</div>
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('mail') }">
							<label class="add-contact__label" for="mail">Adresse email *</label>
							<input
								class="add-contact__input"
								v-validate="'required|email'"
								type="email"
								name="mail"
								id="mail"
								placeholder="jean.dupont@email.com"
								v-model="user.email"
							>
							<span v-if="errors.has('mail')" class="add-contact__error">{{ errors.first('mail') }}</span>
						</div>
					</div>
				</div>

				<!-- Section Adresse -->
				<div class="add-contact__section">
					<div class="add-contact__section-header">
						<svg class="add-contact__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
							<circle cx="12" cy="10" r="3"/>
						</svg>
						<span class="add-contact__section-title">Adresse</span>
					</div>
					<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('address') }">
						<label class="add-contact__label" for="address">Adresse *</label>
						<input
							class="add-contact__input"
							v-validate="'required'"
							type="text"
							name="address"
							id="address"
							placeholder="123 rue de la Paix"
							v-model="user.address.line"
						>
						<span v-if="errors.has('address')" class="add-contact__error">{{ errors.first('address') }}</span>
					</div>
					<div class="add-contact__grid add-contact__grid--3">
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('zip') }">
							<label class="add-contact__label" for="zip">Code postal *</label>
							<input
								class="add-contact__input"
								v-validate="'required'"
								type="text"
								name="zip"
								id="zip"
								placeholder="75001"
								v-model="user.address.zip"
							>
							<span v-if="errors.has('zip')" class="add-contact__error">{{ errors.first('zip') }}</span>
						</div>
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('city') }">
							<label class="add-contact__label" for="city">Ville *</label>
							<input
								class="add-contact__input"
								v-validate="'required'"
								type="text"
								name="city"
								id="city"
								placeholder="Paris"
								v-model="user.address.city"
							>
							<span v-if="errors.has('city')" class="add-contact__error">{{ errors.first('city') }}</span>
						</div>
						<div class="add-contact__field" :class="{ 'add-contact__field--error': errors.has('country') }">
							<label class="add-contact__label" for="country">Pays *</label>
							<input
								class="add-contact__input"
								v-validate="'required'"
								type="text"
								name="country"
								id="country"
								placeholder="France"
								v-model="user.address.country"
							>
							<span v-if="errors.has('country')" class="add-contact__error">{{ errors.first('country') }}</span>
						</div>
					</div>
				</div>

				<!-- Section Informations commerciales -->
				<div class="add-contact__section">
					<div class="add-contact__section-header">
						<svg class="add-contact__section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
							<path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
						</svg>
						<span class="add-contact__section-title">Informations commerciales</span>
					</div>
					<div class="add-contact__grid add-contact__grid--2">
						<div class="add-contact__field add-contact__field--select">
							<label class="add-contact__label">Origine du contact</label>
							<custom-select :items="origins" :selected="user.origin" :solo="true" @change="user.origin = $event"/>
						</div>
						<div class="add-contact__field add-contact__field--select">
							<label class="add-contact__label">Centres d'intérêts</label>
							<custom-multiselect :values="user.centerOfInterests" :list="interests" :object="true" :getter="'wordpress_tag_name'" :solo="true"/>
						</div>
					</div>
				</div>

				<!-- Footer avec boutons -->
				<div class="add-contact__footer">
					<button type="button" class="add-contact__btn add-contact__btn--cancel" @click="close">
						Annuler
					</button>
					<button type="submit" class="add-contact__btn add-contact__btn--submit">
						<svg class="add-contact__btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
							<polyline points="22 4 12 14.01 9 11.01"/>
						</svg>
						Créer le {{ typeLabel }}
					</button>
				</div>
			</form>
		</div>
  </div>
</template>



<style lang="scss" scoped>
// Variables
$color-primary: #8B7355;
$color-primary-dark: #6B5A45;
$color-primary-light: #A89070;
$color-danger: #9B2335;
$color-danger-dark: #7B1A28;
$color-text: #2D3436;
$color-text-light: #636E72;
$color-text-muted: #9CA3AF;
$color-border: #E5E7EB;
$color-border-focus: #8B7355;
$color-bg: #FFFFFF;
$color-bg-section: #FAFAFA;
$color-error: #DC2626;

$radius-sm: 6px;
$radius-md: 8px;
$radius-lg: 12px;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

$transition: all 0.2s ease;

// Main container
.add-contact {
	display: flex;
	flex-direction: column;
	max-height: 85vh;
	background: $color-bg;
	border-radius: $radius-lg;
	overflow: hidden;

	// Header
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1.5rem 2rem;
		border-bottom: 1px solid $color-border;
		background: linear-gradient(135deg, #FDFCFB 0%, #F5F3F0 100%);
	}

	&__header-left {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	&__title {
		margin: 0;
		font-size: 1.25rem;
		font-weight: 600;
		color: $color-text;
		font-family: 'Lato Bold', sans-serif;
	}

	&__subtitle {
		font-size: 0.8rem;
		color: $color-text-muted;
	}

	&__type-selector {
		display: flex;
		background: $color-bg;
		border-radius: $radius-md;
		padding: 4px;
		box-shadow: $shadow-sm;
		border: 1px solid $color-border;
	}

	&__type-btn {
		padding: 0.5rem 1rem;
		border: none;
		background: transparent;
		color: $color-text-light;
		font-size: 0.85rem;
		font-family: 'Lato', sans-serif;
		cursor: pointer;
		border-radius: $radius-sm;
		transition: $transition;

		&:hover {
			color: $color-text;
			background: rgba($color-primary, 0.05);
		}

		&--active {
			background: $color-primary;
			color: white;
			font-family: 'Lato Bold', sans-serif;

			&:hover {
				background: $color-primary-dark;
				color: white;
			}
		}
	}

	// Body
	&__body {
		flex: 1;
		overflow-y: auto;
		padding: 1.5rem 2rem;
	}

	&__form {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	// Sections
	&__section {
		background: $color-bg-section;
		border: 1px solid $color-border;
		border-radius: $radius-md;
		padding: 1.25rem;
	}

	&__section-header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 1rem;
		padding-bottom: 0.75rem;
		border-bottom: 1px solid $color-border;
	}

	&__section-icon {
		width: 18px;
		height: 18px;
		color: $color-primary;
	}

	&__section-title {
		font-size: 0.85rem;
		font-family: 'Lato Bold', sans-serif;
		color: $color-text;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	// Grid layouts
	&__grid {
		display: grid;
		gap: 1rem;

		&--2 {
			grid-template-columns: repeat(2, 1fr);
		}

		&--3 {
			grid-template-columns: 1fr 1.5fr 1fr;
		}
	}

	// Form fields
	&__field {
		display: flex;
		flex-direction: column;
		gap: 0.35rem;

		&--error {
			.add-contact__input {
				border-color: $color-error;
				background: rgba($color-error, 0.02);

				&:focus {
					border-color: $color-error;
					box-shadow: 0 0 0 3px rgba($color-error, 0.1);
				}
			}

			.add-contact__label {
				color: $color-error;
			}
		}

		&--select {
			::v-deep .custom-select,
			::v-deep .custom-multiselect {
				border: 1px solid $color-border;
				border-radius: $radius-sm;
				background: $color-bg;
				transition: $transition;

				&:hover {
					border-color: $color-primary-light;
				}
			}
		}
	}

	&__label {
		font-size: 0.7rem;
		font-family: 'Lato Bold', sans-serif;
		color: $color-text-light;
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}

	&__input {
		width: 100%;
		padding: 0.65rem 0.85rem;
		border: 1px solid $color-border;
		border-radius: $radius-sm;
		font-size: 0.9rem;
		font-family: 'Lato', sans-serif;
		color: $color-text;
		background: $color-bg;
		transition: $transition;
		box-sizing: border-box;

		&::placeholder {
			color: $color-text-muted;
		}

		&:hover {
			border-color: $color-primary-light;
		}

		&:focus {
			outline: none;
			border-color: $color-border-focus;
			box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
		}
	}

	&__error {
		font-size: 0.7rem;
		color: $color-error;
		margin-top: 0.15rem;
	}

	// Footer
	&__footer {
		display: flex;
		justify-content: flex-end;
		gap: 0.75rem;
		padding-top: 1rem;
		margin-top: 0.5rem;
		border-top: 1px solid $color-border;
	}

	&__btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		padding: 0.7rem 1.5rem;
		border: none;
		border-radius: $radius-sm;
		font-size: 0.9rem;
		font-family: 'Lato Bold', sans-serif;
		cursor: pointer;
		transition: $transition;

		&--cancel {
			background: transparent;
			color: $color-text-light;
			border: 1px solid $color-border;

			&:hover {
				background: $color-bg-section;
				border-color: $color-text-muted;
			}
		}

		&--submit {
			background: $color-danger;
			color: white;

			&:hover {
				background: $color-danger-dark;
				transform: translateY(-1px);
				box-shadow: $shadow-md;
			}

			&:active {
				transform: translateY(0);
			}
		}
	}

	&__btn-icon {
		width: 16px;
		height: 16px;
	}
}

// Responsive
@media (max-width: 600px) {
	.add-contact {
		&__header {
			flex-direction: column;
			gap: 1rem;
			align-items: flex-start;
		}

		&__grid--2,
		&__grid--3 {
			grid-template-columns: 1fr;
		}

		&__body {
			padding: 1rem;
		}

		&__footer {
			flex-direction: column-reverse;

			.add-contact__btn {
				width: 100%;
			}
		}
	}
}
</style>



// WEBPACK FOOTER //
// AddContact.vue?5fb4be2a